import { TabsItemType } from "@zapier/design-system";
import { useCallback, useEffect, useMemo, useState } from "react";

export type TabMap = Partial<
  Record<string, Omit<TabsItemType, "href"> & { body: React.ReactNode }>
>;

interface UseTabsReturn {
  activeTabBody: React.ReactNode;
  activeTabHref: string;
  activeTabKey: string;
  setActiveTab: (tabKey: string) => void;
  tabItems: TabsItemType[];
}

function tabHref(tabKey: string): string {
  if (!globalThis.location) {
    return `#${tabKey}`;
  }

  const newUrl = new URL(globalThis.location.href);
  newUrl.hash = `#${tabKey}`;
  return newUrl.href;
}

function useHash(fallback: string): string {
  const [hash, setHash] = useState<string>(
    globalThis.location?.hash.slice(1) || fallback,
  );

  useEffect(() => {
    const handleHashChange = () => {
      const newHash = globalThis.location.hash.slice(1) || fallback;
      setHash(newHash);
    };

    globalThis.addEventListener("hashchange", handleHashChange);

    return () => {
      globalThis.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  return hash;
}

/**
 * Helper to manage tab state based on URL hash.
 *
 * This is needed because the @zapier/design-system Tabs component only supports
 * hrefs, not onClick handlers. This helper sort of hides that and just gives
 * you the active tab and passes the correct href to the Tabs component.
 */
export function useTabs(tabs: TabMap): UseTabsReturn {
  const tabList = Object.entries(tabs);

  const setActiveTab = useCallback((tabKey: string) => {
    globalThis.location.hash = `#${tabKey}`;
  }, []);

  const [defaultTabKey] = tabList[0];
  const activeTabKey = useHash(defaultTabKey);
  const activeTab = tabs[activeTabKey];

  const tabItems: TabsItemType[] = useMemo(() => {
    return tabList.map(([key, tab]) => {
      // Forward the rest of the `TabsItemType` fields a caller set (ariaLabel,
      // disabled, tagText, …); only `body` is ours and must not reach `Tabs`.
      const { body: _body, ...itemProps } = tab ?? {};
      return {
        ...itemProps,
        key,
        text: tab?.text,
        href: tabHref(key),
      };
    });
  }, [tabList]);

  return {
    activeTabBody: activeTab?.body,
    activeTabHref: tabHref(activeTabKey),
    activeTabKey,
    setActiveTab,
    tabItems,
  };
}
